import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { type Observable, map } from 'rxjs';

import type { ApiEnvelope } from '../http/api-envelope';
import type { HelpAnswer, HelpArticle, HelpQuestion, HelpStatus, HelpSummary } from './help.models';

/** clrAIREST: the help topics and the Ask Celeri assistant. */
@Injectable()
export abstract class HelpApi {
  abstract status(): Observable<HelpStatus>;
  abstract list(query: string): Observable<readonly HelpSummary[]>;
  abstract article(id: string): Observable<HelpArticle>;
  abstract ask(question: string): Observable<HelpAnswer>;
  abstract rate(questionId: string, helpful: boolean): Observable<void>;
  abstract questions(unansweredOnly: boolean): Observable<readonly HelpQuestion[]>;
}

@Injectable()
export class HttpHelpApi extends HelpApi {
  private readonly http = inject(HttpClient);

  override status(): Observable<HelpStatus> {
    return this.http
      .get<ApiEnvelope<HelpStatus | null>>('/ai/help/status')
      .pipe(map((response) => response.data ?? { ready: false, log: false }));
  }

  override list(query: string): Observable<readonly HelpSummary[]> {
    const params = query ? new HttpParams().set('q', query) : undefined;

    return this.http
      .get<ApiEnvelope<readonly HelpSummary[] | null>>('/ai/help/articles', { params })
      .pipe(map((response) => response.data ?? []));
  }

  override article(id: string): Observable<HelpArticle> {
    return this.http
      .get<ApiEnvelope<HelpArticle>>(`/ai/help/articles/${encodeURIComponent(id)}`)
      .pipe(map((response) => response.data));
  }

  override ask(question: string): Observable<HelpAnswer> {
    return this.http
      .post<ApiEnvelope<HelpAnswer>>('/ai/help/ask', { question })
      .pipe(map((response) => response.data));
  }

  override rate(questionId: string, helpful: boolean): Observable<void> {
    return this.http
      .post<void>(`/ai/help/questions/${encodeURIComponent(questionId)}/feedback`, { helpful })
      .pipe(map(() => undefined));
  }

  override questions(unansweredOnly: boolean): Observable<readonly HelpQuestion[]> {
    const params = unansweredOnly ? new HttpParams().set('unanswered', 'true') : undefined;

    return this.http
      .get<ApiEnvelope<readonly HelpQuestion[] | null>>('/ai/help/questions', { params })
      .pipe(map((response) => response.data ?? []));
  }
}
